import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/router"
import type { MDXRemoteSerializeResult } from "next-mdx-remote"
import { useRef } from "react"
import { twMerge } from "tailwind-merge"
import MarketingHeading from "@/components/common/MarketingHeading"
import { MarkdownContentRenderer } from "@/components/resources/components/MarkdownContentRenderer"
import { makeButtonClassName } from "@/components/resources/components/ui/Button"
import type { CategoryType, Teaser } from "@/components/resources/data/posts.types"
import { BASE_BLOG_PATH } from "@/constants/urls"
import { BlogImage } from "../components/BlogImage"
import { formatDate } from "../components/ui/utils"

/** The changelog lists whole posts, so each entry carries its compiled body */
export interface ChangelogEntry extends Teaser {
  mdxSource: MDXRemoteSerializeResult
}

interface Props {
  title: string
  entries: ChangelogEntry[]
  lead: string
  category?: CategoryType
}

const PER_PAGE = 9

interface PaginationProps {
  page: number
  nextPage: number
  limit: number
  postsRemaining: boolean
  latestNewIndex: number
}

type UsePaginationProps<T> = [T[], PaginationProps]

const useRoutedPagination = <T,>(
  items: T[],
  { initialOffset = 0 }: { initialOffset?: number } = {}, // Offset allows considering eg. newsletter which is not included in items
): UsePaginationProps<T> => {
  const {
    query: { p },
  } = useRouter()
  const page = Number.parseInt((p && Array.isArray(p) ? p[0] : p) ?? "0", 10)
  const limit = (page ? Math.min(items.length, PER_PAGE * (page + 1)) : PER_PAGE) + initialOffset

  return [
    items.slice(0, limit),
    {
      page,
      nextPage: page + 1,
      limit,
      postsRemaining: items.length + initialOffset > limit,
      latestNewIndex: limit - PER_PAGE + 1 - initialOffset,
    },
  ]
}

const formatTimelineDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const ShowMore = ({ nextPage, postsRemaining }: PaginationProps) => {
  // Relative to the route, not the url: on `/resources/[category]` a bare `?p=1` cannot be resolved
  const { pathname, query } = useRouter()
  return (
    <Link
      href={{ pathname, query: { ...query, p: nextPage } }}
      scroll={false}
      shallow
      className={twMerge(
        makeButtonClassName("medium", "default", "neutral"),
        "mt-4",
        !postsRemaining ? "opacity-50 cursor-not-allowed pointer-events-none" : "",
      )}
      rel="next"
    >
      Load more
    </Link>
  )
}

export const ChangelogCategoryArchive = ({ entries }: Props) => {
  const ref = useRef<null | HTMLDivElement>(null)

  const [shownPosts, pagination] = useRoutedPagination(entries, {
    initialOffset: 0,
  })

  return (
    <article className="flex flex-col mb-16 mx-auto max-w-4xl">
      {entries.length ? (
        <div ref={ref} className="mt-16">
          <div className="relative">
            {/* Timeline entries */}
            <div className="flex flex-col gap-16">
              {shownPosts.map((entry) => {
                const date = entry.publishedAt ? new Date(entry.publishedAt) : null

                return (
                  <div key={entry.id ?? entry.slug} className="relative flex gap-8">
                    {/* Date column - only show on desktop */}
                    <div className="w-[200px] flex-shrink-0 pt-1 justify-start hidden sm:flex items-end h-[theme(fontSize.3xl)]">
                      {date ? (
                        <time
                          dateTime={formatDate(date)}
                          className="text-sm text-fg-secondary font-medium"
                        >
                          {formatTimelineDate(date)}
                        </time>
                      ) : null}
                    </div>
                    {/* Content column */}
                    <div className="flex-1 pb-8 pl-0 sm:pl-8 relative group">
                      {/* Date under the heading in mobile */}
                      {date ? (
                        <time
                          dateTime={formatDate(date)}
                          className="block sm:hidden text-sm text-fg-secondary mb-2"
                        >
                          {formatTimelineDate(date)}
                        </time>
                      ) : null}
                      <Link
                        href={`${BASE_BLOG_PATH}${entry.category ? `/${entry.category}` : ""}/${entry.slug}`}
                        className="block mb-3 sm:mb-5"
                      >
                        <MarketingHeading level={1}>{entry.title}</MarketingHeading>
                      </Link>
                      {typeof entry.image === "string" ? (
                        <Image
                          src={entry.image}
                          alt={entry.title}
                          className="w-full rounded-lg aspect-blog-cover object-cover object-center mb-8"
                          width={1200}
                          height={800}
                        />
                      ) : (
                        <BlogImage
                          width="413"
                          height="592"
                          alt={entry.title}
                          className="w-full rounded-lg aspect-blog-cover object-cover object-center mb-8"
                          {...{
                            category: entry.category,
                            image: entry.image,
                            releaseNumber: entry.releaseNumber,
                            projectMeta: entry.projectMeta,
                          }}
                        />
                      )}
                      <div className="changelog max-w-none [&_p]:mb-3 [&_p:last-child]:mb-0 relative z-[2] [&_a]:pointer-events-auto [&>*]:pointer-events-none">
                        <MarkdownContentRenderer source={entry.mdxSource} />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Load more button */}
            {entries.length > PER_PAGE ? (
              <div className="pt-4 pb-8 flex justify-center">
                <ShowMore {...pagination} />
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="py-12 text-center">
          <p>No posts in this category.</p>
        </div>
      )}
    </article>
  )
}
